import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  ConfigMapDetail,
  GroupBlockDto,
  GroupBlockEntityTypeDto,
  EntityNormalizationDto,
  CategorizationLinkDto,
  DocumentTypeDto,
  EntityTypeDto,
  CategorizationDto,
} from "../interfaces/configMap";

import { newTempId } from "../utils/uuid";

// New records added to the local draft get a temp_<uuid> id so they're
// distinguishable from persisted records in Redux. Stripped to null at the
// API send boundary in useConfigMapDraft.handleSave.
const generateId = () => newTempId();

const findEntityTypeInDraft = (
  draft: ConfigMapDetail,
  id: string,
): EntityTypeDto | undefined => {
  for (const gb of draft.groupBlocks) {
    for (const e of gb.groupBlockEntityTypes) {
      if (e.entityType?.id === id) {
        return JSON.parse(JSON.stringify(e.entityType));
      }
    }
  }
  return undefined;
};

const applyEditedEntityTypeToDraft = (
  draft: ConfigMapDetail,
  id: string,
  edited: EntityTypeDto,
) => {
  for (const gb of draft.groupBlocks) {
    for (const e of gb.groupBlockEntityTypes) {
      if (e.entityType?.id === id) {
        e.entityType = {
          ...e.entityType,
          ...edited,
          categorizationLinks: e.entityType.categorizationLinks,
          entityNormalizations: e.entityType.entityNormalizations,
        };
      }
    }
  }
};

const findCategorizationInDraft = (
  draft: ConfigMapDetail,
  id: string,
): CategorizationDto | undefined => {
  const links: CategorizationLinkDto[] = [];
  links.push(...(draft.categorizationLinks || []));
  for (const gb of draft.groupBlocks) {
    links.push(...(gb.categorizationLinks || []));
    for (const e of gb.groupBlockEntityTypes) {
      links.push(...(e.entityType?.categorizationLinks || []));
    }
  }
  const found = links.find((l) => l.categorization?.id === id);
  return found ? JSON.parse(JSON.stringify(found.categorization)) : undefined;
};

const applyEditedCategorizationToDraft = (
  draft: ConfigMapDetail,
  id: string,
  edited: CategorizationDto,
) => {
  const apply = (links?: CategorizationLinkDto[]) => {
    if (!links) return;
    for (const l of links) {
      if (l.categorization?.id === id) {
        l.categorization = { ...l.categorization, ...edited };
      }
    }
  };
  apply(draft.categorizationLinks);
  for (const gb of draft.groupBlocks) {
    apply(gb.categorizationLinks);
    for (const e of gb.groupBlockEntityTypes) {
      apply(e.entityType?.categorizationLinks);
    }
  }
};

const findNormalizationInDraft = (
  draft: ConfigMapDetail,
  id: string,
): EntityNormalizationDto | undefined => {
  for (const gb of draft.groupBlocks) {
    for (const e of gb.groupBlockEntityTypes) {
      const norm = (e.entityType?.entityNormalizations || []).find(
        (n) => n.id === id,
      );
      if (norm) return JSON.parse(JSON.stringify(norm));
    }
  }
  return undefined;
};

const applyEditedNormalizationToDraft = (
  draft: ConfigMapDetail,
  id: string,
  edited: EntityNormalizationDto,
) => {
  for (const gb of draft.groupBlocks) {
    for (const e of gb.groupBlockEntityTypes) {
      const norms = e.entityType?.entityNormalizations;
      if (!norms) continue;
      const idx = norms.findIndex((n) => n.id === id);
      if (idx >= 0) {
        norms[idx] = { ...norms[idx], ...edited };
      }
    }
  }
};

export interface ValidationError {
  type: "error" | "warning";
  field: string;
  message: string;
  entityId?: string;
  groupBlockId?: string;
}

interface ConfigMapDraftState {
  draft: ConfigMapDetail | null;
  sourceDraft: ConfigMapDetail | null;
  isDirty: boolean;
  isSaving: boolean;
  activeGroupBlockId: string | null;
  activeEntityId: string | null;
  validationErrors: ValidationError[];
  editedEntityTypes: Record<string, EntityTypeDto>;
  editedCategorizations: Record<string, CategorizationDto>;
  editedNormalizations: Record<string, EntityNormalizationDto>;
}

const initialState: ConfigMapDraftState = {
  draft: null,
  sourceDraft: null,
  isDirty: false,
  isSaving: false,
  activeGroupBlockId: null,
  activeEntityId: null,
  validationErrors: [],
  editedEntityTypes: {},
  editedCategorizations: {},
  editedNormalizations: {},
};

export const configMapDraftSlice = createSlice({
  name: "configMapDraft",
  initialState,
  reducers: {
    initDraft: (state, action: PayloadAction<ConfigMapDetail>) => {
      const data = JSON.parse(JSON.stringify(action.payload));
      const sortLinks = (links: CategorizationLinkDto[] | undefined | null) =>
        [...(links || [])].sort((a, b) => {
          const ai = a?.categorization?.index ?? Number.MAX_SAFE_INTEGER;
          const bi = b?.categorization?.index ?? Number.MAX_SAFE_INTEGER;
          if (ai !== bi) return ai - bi;
          return (a?.id ?? "").localeCompare(b?.id ?? "");
        });
      // Ensure arrays are never null
      data.categorizationLinks = sortLinks(data.categorizationLinks);
      data.groupBlocks = (data.groupBlocks || []).map((gb: GroupBlockDto) => ({
        ...gb,
        categorizationLinks: sortLinks(gb.categorizationLinks),
        groupBlockEntityTypes: (gb.groupBlockEntityTypes || []).map(
          (e: GroupBlockEntityTypeDto) => ({
            ...e,
            entityType: {
              ...e.entityType,
              categorizationLinks: sortLinks(e.entityType?.categorizationLinks),
              entityNormalizations: e.entityType?.entityNormalizations || [],
            },
          }),
        ),
      }));
      state.draft = data;
      state.sourceDraft = JSON.parse(JSON.stringify(data));
      state.isDirty = false;
      state.validationErrors = [];
      state.activeGroupBlockId = null;
      state.activeEntityId = null;
      state.editedEntityTypes = {};
      state.editedCategorizations = {};
      state.editedNormalizations = {};
    },

    // Config map metadata
    updateConfigMapDisplayName: (state, action: PayloadAction<string>) => {
      if (!state.draft) return;
      state.draft.display_name = action.payload;
      state.isDirty = true;
    },

    // Document types
    addDocumentTypeToDraft: (state, action: PayloadAction<DocumentTypeDto>) => {
      if (!state.draft) return;
      if (!state.draft.documentTypes) state.draft.documentTypes = [];
      state.draft.documentTypes.push(action.payload);
      state.isDirty = true;
    },

    removeDocumentTypeFromDraft: (state, action: PayloadAction<string>) => {
      if (!state.draft || !state.draft.documentTypes) return;
      state.draft.documentTypes = state.draft.documentTypes.filter(
        (dt) => dt.id !== action.payload,
      );
      state.isDirty = true;
    },

    // Group Block reducers
    updateGroupBlock: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        changes: Partial<GroupBlockDto>;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, changes } = action.payload;
      const index = state.draft.groupBlocks.findIndex(
        (gb) => gb.id === groupBlockId,
      );
      if (index !== -1) {
        state.draft.groupBlocks[index] = {
          ...state.draft.groupBlocks[index],
          ...changes,
        };
        state.isDirty = true;
      }
    },

    addGroupBlock: (
      state,
      action: PayloadAction<{
        translations: Record<string, string>;
        multipleGroupBlocks?: boolean;
        shortDescription?: string;
        longDescription?: string;
      }>,
    ) => {
      if (!state.draft) return;
      const newGroupBlock: GroupBlockDto = {
        id: generateId(),
        index: state.draft.groupBlocks.length,
        translations: action.payload.translations,
        multipleGroupBlocks: action.payload.multipleGroupBlocks ?? false,
        shortDescription: action.payload.shortDescription,
        longDescription: action.payload.longDescription,
        groupBlockEntityTypes: [],
        categorizationLinks: [],
      };
      state.draft.groupBlocks.push(newGroupBlock);
      state.isDirty = true;
    },

    removeGroupBlock: (state, action: PayloadAction<string>) => {
      if (!state.draft) return;
      // Sort by current index first so re-indexing preserves visual order.
      const remaining = [...state.draft.groupBlocks]
        .sort((a, b) => a.index - b.index)
        .filter((gb) => gb.id !== action.payload);
      remaining.forEach((gb, i) => {
        gb.index = i;
      });
      state.draft.groupBlocks = remaining;
      state.isDirty = true;
    },

    reorderGroupBlocks: (
      state,
      action: PayloadAction<{ oldIndex: number; newIndex: number }>,
    ) => {
      if (!state.draft) return;
      const { oldIndex, newIndex } = action.payload;
      // Sort by index first so oldIndex/newIndex match the visual order
      const blocks = [...state.draft.groupBlocks].sort(
        (a, b) => a.index - b.index,
      );
      const [moved] = blocks.splice(oldIndex, 1);
      blocks.splice(newIndex, 0, moved);
      blocks.forEach((gb, i) => {
        gb.index = i;
      });
      state.draft.groupBlocks = blocks;
      state.isDirty = true;
    },

    // Entity reducers
    updateEntity: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        entityId: string;
        changes: Partial<GroupBlockEntityTypeDto>;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, entityId, changes } = action.payload;
      const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
      if (!gb) return;
      const entityIndex = gb.groupBlockEntityTypes.findIndex(
        (e) => e.id === entityId,
      );
      if (entityIndex !== -1) {
        gb.groupBlockEntityTypes[entityIndex] = {
          ...gb.groupBlockEntityTypes[entityIndex],
          ...changes,
        };
        state.isDirty = true;
      }
    },

    updateEntityType: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        entityId: string;
        changes: Partial<GroupBlockEntityTypeDto["entityType"]>;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, entityId, changes } = action.payload;
      const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
      if (!gb) return;
      const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
      if (entity) {
        if (changes && changes.id && changes.id !== entity.entityType?.id) {
          entity.entityType = changes as GroupBlockEntityTypeDto["entityType"];
        } else {
          entity.entityType = { ...entity.entityType, ...changes };
        }
        state.isDirty = true;
      }
    },

    addEntity: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        entityType: GroupBlockEntityTypeDto["entityType"];
        hotkey?: string;
        color?: string;
        required?: boolean;
        deprecated?: boolean;
        widthInPx?: number;
        parentId?: { id: string };
      }>,
    ) => {
      if (!state.draft) return;
      const gb = state.draft.groupBlocks.find(
        (g) => g.id === action.payload.groupBlockId,
      );
      if (!gb) return;
      const maxIndex = gb.groupBlockEntityTypes.reduce(
        (m, e) => (e.index > m ? e.index : m),
        -1,
      );
      const newEntity: GroupBlockEntityTypeDto = {
        id: generateId(),
        entityType: action.payload.entityType,
        index: maxIndex + 1,
        hotkey: action.payload.hotkey ?? "",
        color: action.payload.color ?? "#1677ff",
        required: action.payload.required ?? false,
        deprecated: action.payload.deprecated ?? false,
        widthInPx: action.payload.widthInPx,
        parentId: action.payload.parentId,
      };
      gb.groupBlockEntityTypes.push(newEntity);
      state.isDirty = true;
    },

    removeEntity: (
      state,
      action: PayloadAction<{ groupBlockId: string; entityId: string }>,
    ) => {
      if (!state.draft) return;
      const gb = state.draft.groupBlocks.find(
        (g) => g.id === action.payload.groupBlockId,
      );
      if (!gb) return;
      // Sort by current index first so re-indexing preserves visual order.
      const remaining = [...gb.groupBlockEntityTypes]
        .sort((a, b) => a.index - b.index)
        .filter((e) => e.id !== action.payload.entityId);
      remaining.forEach((e, i) => {
        e.index = i;
      });
      gb.groupBlockEntityTypes = remaining;
      state.isDirty = true;
    },

    reorderEntities: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        oldIndex: number;
        newIndex: number;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, oldIndex, newIndex } = action.payload;
      const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
      if (!gb) return;
      // Sort by index first so oldIndex/newIndex match the visual order
      const entities = [...gb.groupBlockEntityTypes].sort(
        (a, b) => a.index - b.index,
      );
      const [moved] = entities.splice(oldIndex, 1);
      entities.splice(newIndex, 0, moved);
      entities.forEach((e, i) => {
        e.index = i;
      });
      gb.groupBlockEntityTypes = entities;
      state.isDirty = true;
    },

    // Normalization reducers
    addNormalization: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        entityId: string;
        normalization: EntityNormalizationDto;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, entityId, normalization } = action.payload;
      const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
      if (!gb) return;
      const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
      if (!entity) return;
      if (!entity.entityType.entityNormalizations) {
        entity.entityType.entityNormalizations = [];
      }
      entity.entityType.entityNormalizations.push(normalization);
      state.isDirty = true;
    },

    updateNormalization: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        entityId: string;
        normalizationId: string;
        changes: Partial<EntityNormalizationDto>;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, entityId, normalizationId, changes } =
        action.payload;
      const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
      if (!gb) return;
      const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
      if (!entity?.entityType.entityNormalizations) return;
      const normIndex = entity.entityType.entityNormalizations.findIndex(
        (n) => n.id === normalizationId,
      );
      if (normIndex !== -1) {
        entity.entityType.entityNormalizations[normIndex] = {
          ...entity.entityType.entityNormalizations[normIndex],
          ...changes,
        };
        state.isDirty = true;
      }
    },

    removeNormalization: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        entityId: string;
        normalizationId: string;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, entityId, normalizationId } = action.payload;
      const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
      if (!gb) return;
      const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
      if (!entity?.entityType.entityNormalizations) return;
      entity.entityType.entityNormalizations =
        entity.entityType.entityNormalizations.filter(
          (n) => n.id !== normalizationId,
        );
      state.isDirty = true;
    },

    reorderNormalizations: (
      state,
      action: PayloadAction<{
        groupBlockId: string;
        entityId: string;
        oldIndex: number;
        newIndex: number;
      }>,
    ) => {
      if (!state.draft) return;
      const { groupBlockId, entityId, oldIndex, newIndex } = action.payload;
      const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
      if (!gb) return;
      const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
      if (!entity?.entityType.entityNormalizations) return;
      const norms = [...entity.entityType.entityNormalizations];
      const [moved] = norms.splice(oldIndex, 1);
      norms.splice(newIndex, 0, moved);
      norms.forEach((n, i) => {
        n.index = i;
      });
      entity.entityType.entityNormalizations = norms;
      state.isDirty = true;
    },

    // Categorization link reducers
    addCategorizationLink: (
      state,
      action: PayloadAction<{
        level: "configmap" | "groupBlock" | "entity";
        groupBlockId?: string;
        entityId?: string;
        link: CategorizationLinkDto;
      }>,
    ) => {
      if (!state.draft) return;
      const { level, groupBlockId, entityId, link } = action.payload;

      if (level === "configmap") {
        if (!state.draft.categorizationLinks) {
          state.draft.categorizationLinks = [];
        }
        state.draft.categorizationLinks.push(link);
      } else if (level === "groupBlock" && groupBlockId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (gb) {
          if (!gb.categorizationLinks) {
            gb.categorizationLinks = [];
          }
          gb.categorizationLinks.push(link);
        }
      } else if (level === "entity" && groupBlockId && entityId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (!gb) {
          return;
        }
        const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
        if (entity) {
          if (!entity.entityType.categorizationLinks) {
            entity.entityType.categorizationLinks = [];
          }
          entity.entityType.categorizationLinks.push(link);
        }
      }
      state.isDirty = true;
    },

    removeCategorizationLink: (
      state,
      action: PayloadAction<{
        level: "configmap" | "groupBlock" | "entity";
        groupBlockId?: string;
        entityId?: string;
        linkId: string;
      }>,
    ) => {
      if (!state.draft) return;
      const { level, groupBlockId, entityId, linkId } = action.payload;

      if (level === "configmap") {
        state.draft.categorizationLinks =
          state.draft.categorizationLinks.filter((l) => l.id !== linkId);
      } else if (level === "groupBlock" && groupBlockId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (gb) {
          gb.categorizationLinks = gb.categorizationLinks.filter(
            (l) => l.id !== linkId,
          );
        }
      } else if (level === "entity" && groupBlockId && entityId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (!gb) return;
        const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
        if (entity?.entityType.categorizationLinks) {
          entity.entityType.categorizationLinks =
            entity.entityType.categorizationLinks.filter(
              (l) => l.id !== linkId,
            );
        }
      }
      state.isDirty = true;
    },

    updateCategorizationLink: (
      state,
      action: PayloadAction<{
        level: "configmap" | "groupBlock" | "entity";
        groupBlockId?: string;
        entityId?: string;
        link: CategorizationLinkDto;
      }>,
    ) => {
      if (!state.draft) return;
      const { level, groupBlockId, entityId, link } = action.payload;

      const replaceLink = (links: CategorizationLinkDto[]) =>
        links.map((l) => (l.id === link.id ? link : l));

      if (level === "configmap") {
        state.draft.categorizationLinks = replaceLink(
          state.draft.categorizationLinks,
        );
      } else if (level === "groupBlock" && groupBlockId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (gb) {
          gb.categorizationLinks = replaceLink(gb.categorizationLinks);
        }
      } else if (level === "entity" && groupBlockId && entityId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (!gb) return;
        const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
        if (entity?.entityType.categorizationLinks) {
          entity.entityType.categorizationLinks = replaceLink(
            entity.entityType.categorizationLinks,
          );
        }
      }
      state.isDirty = true;
    },

    reorderCategorizationLinks: (
      state,
      action: PayloadAction<{
        level: "configmap" | "groupBlock" | "entity";
        groupBlockId?: string;
        entityId?: string;
        oldIndex: number;
        newIndex: number;
      }>,
    ) => {
      if (!state.draft) return;
      const { level, groupBlockId, entityId, oldIndex, newIndex } =
        action.payload;

      const reorder = (links: CategorizationLinkDto[]) => {
        const arr = [...links];
        const [moved] = arr.splice(oldIndex, 1);
        arr.splice(newIndex, 0, moved);
        return arr;
      };

      if (level === "configmap") {
        state.draft.categorizationLinks = reorder(
          state.draft.categorizationLinks,
        );
      } else if (level === "groupBlock" && groupBlockId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (gb) {
          gb.categorizationLinks = reorder(gb.categorizationLinks);
        }
      } else if (level === "entity" && groupBlockId && entityId) {
        const gb = state.draft.groupBlocks.find((g) => g.id === groupBlockId);
        if (!gb) return;
        const entity = gb.groupBlockEntityTypes.find((e) => e.id === entityId);
        if (entity?.entityType.categorizationLinks) {
          entity.entityType.categorizationLinks = reorder(
            entity.entityType.categorizationLinks,
          );
        }
      }
      state.isDirty = true;
    },

    updateConfigmapCategorizationLinks: (
      state,
      action: PayloadAction<CategorizationLinkDto[]>,
    ) => {
      if (!state.draft) return;
      state.draft.categorizationLinks = action.payload;
      state.isDirty = true;
    },

    // Edited shared records — staged in draft, flushed via PUT on Save
    updateEntityTypeRecord: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<EntityTypeDto> }>,
    ) => {
      if (!state.draft) return;
      const { id, changes } = action.payload;
      const existing = state.editedEntityTypes[id];
      const base =
        existing ??
        findEntityTypeInDraft(state.draft, id) ??
        ({ id } as EntityTypeDto);
      state.editedEntityTypes[id] = {
        ...base,
        ...changes,
        id,
        _isEdited: true,
      };
      applyEditedEntityTypeToDraft(
        state.draft,
        id,
        state.editedEntityTypes[id],
      );
      state.isDirty = true;
    },

    updateCategorizationRecord: (
      state,
      action: PayloadAction<{
        id: string;
        changes: Partial<CategorizationDto>;
      }>,
    ) => {
      if (!state.draft) return;
      const { id, changes } = action.payload;
      const existing = state.editedCategorizations[id];
      const base =
        existing ??
        findCategorizationInDraft(state.draft, id) ??
        ({ id } as CategorizationDto);
      state.editedCategorizations[id] = {
        ...base,
        ...changes,
        id,
        _isEdited: true,
      };
      applyEditedCategorizationToDraft(
        state.draft,
        id,
        state.editedCategorizations[id],
      );
      state.isDirty = true;
    },

    updateNormalizationRecord: (
      state,
      action: PayloadAction<{
        id: string;
        entityTypeId: string;
        changes: Partial<EntityNormalizationDto>;
      }>,
    ) => {
      if (!state.draft) return;
      const { id, entityTypeId, changes } = action.payload;
      const existing = state.editedNormalizations[id];
      const base =
        existing ??
        findNormalizationInDraft(state.draft, id) ??
        ({
          id,
          entityTypeId,
          index: 0,
          normalizationStrategy: {},
        } as EntityNormalizationDto);
      state.editedNormalizations[id] = {
        ...base,
        ...changes,
        id,
        entityTypeId,
        _isEdited: true,
      };
      applyEditedNormalizationToDraft(
        state.draft,
        id,
        state.editedNormalizations[id],
      );
      state.isDirty = true;
    },

    // Draft management
    resetDraft: (state) => {
      if (!state.sourceDraft) return;
      state.draft = JSON.parse(JSON.stringify(state.sourceDraft));
      state.isDirty = false;
      state.activeGroupBlockId = null;
      state.activeEntityId = null;
      state.validationErrors = [];
      state.editedEntityTypes = {};
      state.editedCategorizations = {};
      state.editedNormalizations = {};
    },

    // Wipe the draft entirely (used when the consumer unmounts so the next
    // visit re-initializes from fresh server data rather than reusing a stale
    // persisted draft).
    clearDraft: (state) => {
      state.draft = null;
      state.sourceDraft = null;
      state.isDirty = false;
      state.activeGroupBlockId = null;
      state.activeEntityId = null;
      state.validationErrors = [];
      state.editedEntityTypes = {};
      state.editedCategorizations = {};
      state.editedNormalizations = {};
    },

    markClean: (state) => {
      if (state.draft) {
        state.sourceDraft = JSON.parse(JSON.stringify(state.draft));
      }
      state.isDirty = false;
      state.isSaving = false;
      state.editedEntityTypes = {};
      state.editedCategorizations = {};
      state.editedNormalizations = {};
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },

    setActiveGroupBlockId: (state, action: PayloadAction<string | null>) => {
      state.activeGroupBlockId = action.payload;
    },

    setActiveEntityId: (state, action: PayloadAction<string | null>) => {
      state.activeEntityId = action.payload;
    },

    setValidationErrors: (state, action: PayloadAction<ValidationError[]>) => {
      state.validationErrors = action.payload;
    },
  },
});

export const {
  initDraft,
  updateConfigMapDisplayName,
  updateGroupBlock,
  addGroupBlock,
  removeGroupBlock,
  reorderGroupBlocks,
  updateEntity,
  updateEntityType,
  addEntity,
  removeEntity,
  reorderEntities,
  addNormalization,
  updateNormalization,
  removeNormalization,
  reorderNormalizations,
  addCategorizationLink,
  updateCategorizationLink,
  removeCategorizationLink,
  reorderCategorizationLinks,
  updateConfigmapCategorizationLinks,
  resetDraft,
  clearDraft,
  markClean,
  setSaving,
  setActiveGroupBlockId,
  setActiveEntityId,
  setValidationErrors,
  addDocumentTypeToDraft,
  removeDocumentTypeFromDraft,
  updateEntityTypeRecord,
  updateCategorizationRecord,
  updateNormalizationRecord,
} = configMapDraftSlice.actions;

// Selectors
export const selectDraft = (state: RootState) =>
  state.configMapDraftReducer.draft;
export const selectSourceDraft = (state: RootState) =>
  state.configMapDraftReducer.sourceDraft;
export const selectIsDirty = (state: RootState) =>
  state.configMapDraftReducer.isDirty;
export const selectIsSaving = (state: RootState) =>
  state.configMapDraftReducer.isSaving;
export const selectActiveGroupBlockId = (state: RootState) =>
  state.configMapDraftReducer.activeGroupBlockId;
export const selectActiveEntityId = (state: RootState) =>
  state.configMapDraftReducer.activeEntityId;
export const selectValidationErrors = (state: RootState) =>
  state.configMapDraftReducer.validationErrors;
export const selectEditedEntityTypes = (state: RootState) =>
  state.configMapDraftReducer.editedEntityTypes;
export const selectEditedCategorizations = (state: RootState) =>
  state.configMapDraftReducer.editedCategorizations;
export const selectEditedNormalizations = (state: RootState) =>
  state.configMapDraftReducer.editedNormalizations;

export default configMapDraftSlice.reducer;
